import * as History from 'history';
import axios, { AxiosResponse, AxiosError } from 'axios';
// import axiosRetry from 'axios-retry';
import { Message } from 'google-protobuf';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';


const SESSION_ID_HEADER = 'x-session-id';
const SESSION_ID_KEY = 'x-session-id';
const ACCEPT_LANGUAGE_HEADER = 'Accept-Language';

export const http = axios;

// axiosRetry(http, { retries: 3, retryDelay: axiosRetry.exponentialDelay });

export function configure(apiHost: string) {
  axios.defaults.withCredentials = true;
  axios.defaults.headers.common['Content-Type'] = 'application/octet-stream';
  if (localStorage.getItem(SESSION_ID_KEY) != null) {
    axios.defaults.headers.common[SESSION_ID_HEADER] = localStorage.getItem(SESSION_ID_KEY);
  }
  axios.defaults.baseURL = apiHost;
}

axios.interceptors.request.use((request) => {
  if (request.headers == null) {
    request.headers = {};
  }
  if (request.headers.common == null) {
    request.headers.common = {};
  }
  request.headers.common[ACCEPT_LANGUAGE_HEADER] = localeToLanguageCode(GlobalLocaleStore.get());
  return request;
});

axios.interceptors.response.use((response) => {
  const sessionID = response.headers[SESSION_ID_HEADER];
  if (sessionID != null) {
    if (sessionID) {
      axios.defaults.headers.common[SESSION_ID_HEADER] = sessionID;
      localStorage.setItem(SESSION_ID_KEY, sessionID);
    } else {
      delete axios.defaults.headers.common[SESSION_ID_HEADER];
      localStorage.removeItem(SESSION_ID_KEY);
    }
  }
  return response;
});

export function setCSRFToken(csrfToken: string) {
  axios.defaults.headers.common['x-csrftoken'] = csrfToken;
}

export function isHttpError(error: unknown): error is AxiosError {
  return (error != null && typeof error === 'object' &&
          'isAxiosError' in error && error.isAxiosError === true);
}

export function getResponseBytes(response: AxiosResponse): Uint8Array {
  // Convert the base64-encoded response to binary so it can be deserialized
  const binary = atob(response.data);
  const data: number[] = [];
  for (let i=0; i<binary.length; i++) {
    data.push(binary.charCodeAt(i));
  }
  return new Uint8Array(data);
}

export function createRequestString(request: Message): string {
  return bytesToBase64(request.serializeBinary());
}

const base64abc: string[] = (() => {
  const abc = [];
  const A = 'A'.charCodeAt(0);
  const a = 'a'.charCodeAt(0);
  const n = '0'.charCodeAt(0);

  for (let i = 0; i < 26; ++i) {
    abc.push(String.fromCharCode(A + i));
  }
  for (let i = 0; i < 26; ++i) {
    abc.push(String.fromCharCode(a + i));
  }
  for (let i = 0; i < 10; ++i) {
    abc.push(String.fromCharCode(n + i));
  }
  abc.push('+');
  abc.push('/');
  return abc;
})();

function bytesToBase64(bytes: Uint8Array): string {
  const length = bytes.length;
  const result: string[] = [];
  let i;
  for (i = 2; i < length; i += 3) {
    result.push(base64abc[bytes[i - 2] >> 2]);
    result.push(base64abc[((bytes[i - 2] & 0x03) << 4) | (bytes[i - 1] >> 4)]);
    result.push(base64abc[((bytes[i - 1] & 0x0F) << 2) | (bytes[i] >> 6)]);
    result.push(base64abc[bytes[i] & 0x3F]);
  }
  if (i === length + 1) { // 1 octet missing
    result.push(base64abc[bytes[i - 2] >> 2]);
    result.push(base64abc[(bytes[i - 2] & 0x03) << 4]);
    result.push('==');
  }
  if (i === length) { // 2 octets missing
    result.push(base64abc[bytes[i - 2] >> 2]);
    result.push(base64abc[((bytes[i - 2] & 0x03) << 4) | (bytes[i - 1] >> 4)]);
    result.push(base64abc[(bytes[i - 1] & 0x0F) << 2]);
    result.push('=');
  }
  return result.join('');
}

export function parseQueryString(location: History.Location) {
  const qs = location.search.substring(1).split('&');
  const result: Record<string, string> = {};
  for (const pair of qs) {
    const [key, value] = pair.split('=');
    result[decodeURIComponent(key)] = decodeURIComponent(value);
  }
  return result;
}
